import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  GET_COACH_FEEDBACK_COUNT,
  GET_LEARNER_FEEDBACK_REQUEST_COUNT,
} from "graphql/feedback/feedback.query";
import { GET_BADGES, GET_USER_BADGES } from "graphql/badge/badge.query";
import { ADD_USER_BADGE } from "graphql/badge/badge.mutation";
import { checkBadgeEligibility } from "utils/badges";
import { useAuthUser } from "hooks/useAuthUser";
import { ROLES } from "types/user";
import { BadgeCriteriaType } from "helpers/constants";

/**
 * Hook to check and award badges when a coach completes feedback or learner requests feedback
 * Call checkAndAwardBadge() after updating feedback status to DONE (coach) or submitting feedback request (learner)
 */
export const useBadgeChecker = () => {
  const { user } = useAuthUser();
  const [getCoachFeedbackCount] = useLazyQuery(GET_COACH_FEEDBACK_COUNT);
  const [getLearnerFeedbackRequestCount] = useLazyQuery(
    GET_LEARNER_FEEDBACK_REQUEST_COUNT,
  );
  const {
    data: badgesData,
    refetch: refetchBadges,
  } = useQuery(GET_BADGES, {
    skip:
      !user?.user_id ||
      (user?.role !== ROLES.COACH && user?.role !== ROLES.STUDENT),
    fetchPolicy: "cache-and-network", // Always get fresh badge data
  });
  const { data: userBadgesData, refetch: refetchUserBadges } = useQuery(
    GET_USER_BADGES,
    {
      variables: { userId: user?.user_id || "" },
      skip:
        !user?.user_id ||
        (user?.role !== ROLES.COACH && user?.role !== ROLES.STUDENT),
    },
  );
  const [addUserBadge] = useMutation(ADD_USER_BADGE);

  const checkAndAwardBadge = async (): Promise<any[] | null> => {
    if (
      !user?.user_id ||
      (user?.role !== ROLES.COACH && user?.role !== ROLES.STUDENT)
    ) {
      return null;
    }

    try {
      // Ensure badges data is loaded - refetch if not available
      let badgesToUse = badgesData?.badge;
      if (!badgesToUse || badgesToUse.length === 0) {
        console.log("Badges data not available, refetching...");
        const badgesResult = await refetchBadges();
        badgesToUse = badgesResult.data?.badge;
        if (!badgesToUse || badgesToUse.length === 0) {
          console.log("No badges found in database");
          return null;
        }
      }
      let count = 0;
      let criteriaType: BadgeCriteriaType = BadgeCriteriaType.FEEDBACK_COUNT;

      if (user.role === ROLES.COACH) {
        // Query feedbacks with status = "DONE" and count them
        const feedbackCountResult = await getCoachFeedbackCount({
          variables: { coachId: user.user_id },
          fetchPolicy: "network-only", // Ensure we get the latest count including the just-completed feedback
        });
        count = feedbackCountResult.data?.feedback?.length || 0;
        criteriaType = BadgeCriteriaType.FEEDBACK_COUNT;
      } else if (user.role === ROLES.STUDENT) {
        // Query all feedbacks requested by the learner
        const feedbackRequestCountResult = await getLearnerFeedbackRequestCount(
          {
            variables: { studentId: user.user_id },
            fetchPolicy: "network-only", // Ensure we get the latest count including the just-requested feedback
          },
        );
        count = feedbackRequestCountResult.data?.feedback?.length || 0;
        criteriaType = BadgeCriteriaType.FEEDBACK_REQUESTED_COUNT;
      }

      let existingBadgeRows = userBadgesData?.user_badge;
      if (!existingBadgeRows) {
        const userBadgesResult = await refetchUserBadges();
        existingBadgeRows = userBadgesResult.data?.user_badge;
      }
      const existingBadgeNames =
        existingBadgeRows?.map((ub: any) => ub.badge?.name || "") || [];

      const availableBadges = (badgesToUse || []) as any[];

      const eligibleBadgeNames = checkBadgeEligibility(
        count,
        existingBadgeNames,
        availableBadges,
        criteriaType,
      );

      if (eligibleBadgeNames.length > 0 && badgesToUse) {
        // Award all eligible badges
        const badgesToAward = eligibleBadgeNames
          .map((badgeName) =>
            badgesToUse.find((b: any) => b.name === badgeName),
          )
          .filter(Boolean);

        // Add all badges and keep only truly new inserts (affected_rows > 0).
        const awardResults = await Promise.all(
          badgesToAward.map((badge: any) =>
            addUserBadge({
              variables: {
                user_id: user.user_id,
                badge_id: badge.badge_id,
              },
            }),
          ),
        );
        refetchUserBadges();

        const newlyAwardedBadgeIds = new Set(
          awardResults
            .map((res: any, index: number) => {
              const affectedRows = res?.data?.insert_user_badge?.affected_rows ?? 0;
              return affectedRows > 0 ? badgesToAward[index]?.badge_id : null;
            })
            .filter(Boolean),
        );

        const newlyAwardedBadges = badgesToAward.filter((badge: any) =>
          newlyAwardedBadgeIds.has(badge.badge_id),
        );

        // Only return badges that were newly inserted now.
        return newlyAwardedBadges.length > 0 ? newlyAwardedBadges : null;
      }
    } catch (error) {
      console.error("Error checking badge eligibility:", error);
      // Don't block feedback submission/approval if badge check fails
    }

    return null;
  };

  return { checkAndAwardBadge };
};
